import { useCallback, useEffect, useState } from "react";
import { brand } from "@/shared/lib/safecheckBrand";
import { fetchAdminDashboard } from "../api/adminApi";
import { getAuthToken, getAuthUser } from "../auth";
import {
  adminDashboardSummary,
  adminInvoiceDistribution,
  adminKeyIndicators,
  adminSystemStats,
  submittedInvoices,
  type SubmittedInvoice,
} from "../data/adminDashboard";

export type AdminDashboardViewData = {
  header: {
    roleBadge: string;
    displayDate: string;
    totalInvoices: number;
    admissions: number;
  };
  summary: {
    totalInvoices: number;
    inReview: number;
    approved: number;
    admissions: number;
    admissionsToday: number;
    admissionsTotal: number;
    admissionsEnCours: number;
  };
  indicators: Array<(typeof adminKeyIndicators)[number] & { value: number }>;
  invoiceDistribution: Array<{ key: string; value: number; color: string }>;
  systemStats: Array<{ id: string; value: string | number; icon: "hospital" | "building" | "auditor" | "rate" }>;
  submittedInvoices: SubmittedInvoice[];
  keyIndicators: { invoicesInReview: number; invoicesApproved: number; requestsPending: number; requestsApproved: number };
  overview: { insurers: number; providers: number; activeInsured: number; activePackages: number; companies: number };
};

const EMPTY_KEY = { invoicesInReview: 0, invoicesApproved: 0, requestsPending: 0, requestsApproved: 0 };
const EMPTY_OVERVIEW = { insurers: 0, providers: 0, activeInsured: 0, activePackages: 0, companies: 0 };

const distributionColors: Record<string, string> = {
  submitted: brand.navy,
  review: brand.navyLight,
  approved: brand.lime,
  paid: brand.limeDark,
  modify: brand.navyDark,
};

const mockData: AdminDashboardViewData = {
  header: {
    roleBadge: "Admin",
    displayDate: "26 juin 2026",
    totalInvoices: adminDashboardSummary.totalInvoices,
    admissions: adminDashboardSummary.admissionsToday,
  },
  summary: {
    totalInvoices: adminDashboardSummary.totalInvoices,
    inReview: adminDashboardSummary.inReview,
    approved: adminDashboardSummary.approved,
    admissions: adminDashboardSummary.admissionsToday,
    admissionsToday: adminDashboardSummary.admissionsToday,
    admissionsTotal: adminDashboardSummary.admissions,
    admissionsEnCours: 0,
  },
  indicators: adminKeyIndicators.map((indicator) => ({ ...indicator, value: indicator.value })),
  keyIndicators: EMPTY_KEY,
  overview: EMPTY_OVERVIEW,
  invoiceDistribution: adminInvoiceDistribution.map((item) => ({ ...item })),
  systemStats: adminSystemStats.map((stat) => ({ ...stat })),
  submittedInvoices,
};

function formatSubmittedDate(createdAt: string | null): string {
  if (!createdAt) return "—";
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return "—";
  return created.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function delayDays(createdAt: string | null): number {
  if (!createdAt) return 0;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 0;
  return Math.max(0, Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24)));
}

export function useAdminDashboard() {
  const [data, setData] = useState<AdminDashboardViewData>(mockData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(false);

  const load = useCallback(async () => {
    if (!getAuthToken()) {
      setUsingApi(false);
      setData(mockData);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    setUsingApi(true);

    try {
      const api = await fetchAdminDashboard();
      const user = getAuthUser();
      const name = api.admin_name || user?.name || "Admin";
      const { summary, system_stats: system } = api;

      setData({
        header: {
          roleBadge: api.header.role_badge || name,
          displayDate: api.header.display_date,
          totalInvoices: api.header.total_invoices,
          admissions: api.header.admissions,
        },
        summary: {
          totalInvoices: summary.total_invoices,
          inReview: summary.in_review,
          approved: summary.approved,
          admissions: summary.admissions_today ?? summary.admissions,
          admissionsToday: summary.admissions_today ?? summary.admissions,
          admissionsTotal: summary.admissions_total ?? summary.admissions,
          admissionsEnCours: summary.admissions_en_cours,
        },
        indicators: adminKeyIndicators.map((indicator) => {
          const value =
            indicator.id === "totalInvoices"
              ? summary.total_invoices
              : indicator.id === "inReview"
                ? summary.in_review
                : indicator.id === "approved"
                  ? summary.approved
                  : indicator.id === "admissions"
                    ? (summary.admissions_today ?? summary.admissions)
                    : indicator.value;
          return { ...indicator, value };
        }),
        invoiceDistribution: api.invoice_distribution.map((item) => ({
          key: item.key,
          value: item.value,
          color: distributionColors[item.key] ?? brand.navy,
        })),
        systemStats: [
          { id: "hospitals", value: system.hospitals, icon: "hospital" },
          { id: "companies", value: system.companies, icon: "building" },
          { id: "auditors", value: system.auditors, icon: "auditor" },
          { id: "validationRate", value: system.validation_rate, icon: "rate" },
        ],
        keyIndicators: api.key_indicators
          ? {
              invoicesInReview: api.key_indicators.invoices_in_review,
              invoicesApproved: api.key_indicators.invoices_approved,
              requestsPending: api.key_indicators.requests_pending,
              requestsApproved: api.key_indicators.requests_approved,
            }
          : EMPTY_KEY,
        overview: api.overview
          ? {
              insurers: api.overview.insurers,
              providers: api.overview.providers,
              activeInsured: api.overview.active_insured,
              activePackages: api.overview.active_packages,
              companies: api.overview.companies,
            }
          : EMPTY_OVERVIEW,
        submittedInvoices: api.recent_submitted.map((row) => ({
          id: `admin-inv-${row.id}`,
          displayId: row.id,
          hospital: row.hospital || "—",
          company: row.company || row.label || "—",
          amount: row.amount_usd,
          submittedDate: formatSubmittedDate(row.created_at),
          delayDays: delayDays(row.created_at),
        })),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Impossible de charger le dashboard.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return { data, loading, error, usingApi, reload: load };
}
